/* =================================================================
   持久化层 — 基于 localStorage
   ================================================================= */

const STORE_KEY = 'sand-world-platform::v1';

function loadState() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return seedInitialState();
    const parsed = JSON.parse(raw);
    // 版本向下兼容: 补齐新字段
    if (!parsed.knowledge) {
      const fresh = seedInitialState();
      parsed.knowledge = fresh.knowledge;
    }
    if (!parsed.anonChats) parsed.anonChats = {};
    if (!parsed.agentOverrides) parsed.agentOverrides = {};
    if (!parsed.weaponOverrides) parsed.weaponOverrides = {};
    Object.values(parsed.students || {}).forEach(s => { if (!s.freeChats) s.freeChats = []; });

    /* 旧数据迁移: 仅刷新系统种子 (kb-seed-*) 中的过期内容, 保留老师自己加的 */
    const STALE_PATTERNS = /南京|紫金山|玄武湖|长江岸|福建土楼|宁波保国寺|苏州沧浪亭/;
    let needRefresh = false;
    ROLES.forEach(r => {
      const list = parsed.knowledge[r.id] || [];
      list.forEach(it => {
        if (it.id?.startsWith('kb-seed-') &&
            (STALE_PATTERNS.test(it.title || '') || STALE_PATTERNS.test(it.content || ''))) {
          needRefresh = true;
        }
      });
    });
    if (needRefresh) {
      console.info('[migration] refreshing stale seed knowledge to Yinchuan version');
      const fresh = seedInitialState();
      ROLES.forEach(r => {
        const teacherAdded = (parsed.knowledge[r.id] || []).filter(it => !it.id?.startsWith('kb-seed-'));
        // 新种子放前面 + 老师自己加的放后面
        parsed.knowledge[r.id] = [...fresh.knowledge[r.id], ...teacherAdded];
      });
    }

    return parsed;
  } catch (e) {
    console.warn('loadState failed, reseeding', e);
    return seedInitialState();
  }
}

function saveState(state) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(state));
  } catch (e) {
    console.warn('saveState failed', e);
    throw new Error('浏览器空间不足，记录未保存。请导出备份；不会自动删除任何附件。');
  }
}

function resetState() {
  localStorage.removeItem(STORE_KEY);
  return seedInitialState();
}

function seedInitialState() {
  /* 把演示学生展开为完整的 progress 数据 */
  const stageOrder = ['learn','ask','think','debate','act'];

  const students = {};
  DEMO_STUDENTS.forEach(s => {
    const currIdx = stageOrder.indexOf(s.progress);
    const completed = stageOrder.slice(0, currIdx);
    const role = ROLES_BY_ID[s.roleId];

    students[s.id] = {
      id: s.id,
      name: s.name,
      class: s.class,
      roleId: s.roleId,
      team: s.team,
      currentStage: s.progress,
      stages: stageOrder.reduce((acc, key) => {
        const isCompleted = completed.includes(key);
        const isCurrent = key === s.progress;
        acc[key] = {
          status: isCompleted ? 'completed' : (isCurrent ? 'in-progress' : 'locked'),
          conversation: isCompleted ? seedConversation(role, key) : [],
          submission: isCompleted ? seedSubmission(role, key, s.name) : { text: '', reflection: '', media: [] },
          aiScore: null,
          teacherScore: null,
          teacherNote: '',
        };
        return acc;
      }, {}),
      portfolioReflection: '',
      createdAt: '2026-05-09',
    };
    /* AI 评分 + 教师评分 (已完成阶段) */
    completed.forEach(key => {
      const stageData = students[s.id].stages[key];
      stageData.aiScore = aiSuggestScore({
        student: students[s.id], role, stage: key,
        submission: stageData.submission, conversation: stageData.conversation,
      });
      stageData.teacherScore = {
        task: clampScore(stageData.aiScore.task + randDelta()),
        thinking: clampScore(stageData.aiScore.thinking + randDelta()),
        evidence: clampScore(stageData.aiScore.evidence + randDelta()),
        express: clampScore(stageData.aiScore.express + randDelta()),
      };
      stageData.teacherNote = pick([
        '继续保持，证据再多一些更好。',
        '思考很有方向，再细化方案。',
        '小组合作不错。',
        '可在路演时多一些眼神交流。',
      ]);
    });
  });

  /* 成果展种子数据 (转为完整结构) */
  const showcase = SHOWCASE_SEED.map(s => ({ ...s, published: true }));

  /* 知识库种子: 把现有课程资料预填到每个智能体的知识库 */
  const knowledge = {};
  ROLES.forEach(r => {
    knowledge[r.id] = r.materials.map((m, i) => ({
      id: `kb-seed-${r.id}-${i}`,
      kind: 'text',
      title: m.title,
      tag: m.type,
      content: m.summary,
      addedAt: '2026-05-09',
      addedBy: '系统种子',
    }));
  });

  return {
    students,
    showcase,
    knowledge,                       // { [roleId]: [{ id, kind, title, content, ... }] }
    anonChats: {},                   // { [roleId]: [{ role, text, at }] } 匿名用户对话
    agentOverrides: {},              // { [roleId]: { identity?, expertise?, signature?, taboo?, patterns?, shots?, catchphrase?, nickname?, mission? } }
    weaponOverrides: {},             // { [weaponId]: { promptAddon?, tagline?, longTagline?, starters?, problemSolved? } }
    session: null,
    teacherPicks: showcase.map(s => s.id),
    notifications: [],
    version: 3,
  };
}

function clampScore(n) { return Math.max(1, Math.min(5, n)); }
function randDelta() { return Math.random() < 0.5 ? 0 : (Math.random() < 0.5 ? 1 : -1); }
function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

/* ---- 假数据：对话 + 提交 ---- */

function seedConversation(role, stage) {
  const stuOpeners = {
    learn:  `我刚刚看完${role.materials[0].title}，最让我惊讶的是${role.keywords[0]}居然能${pick(['决定一片土地','改变整片地形','支撑整座城','解释一种现象'])}`,
    ask:    `我想知道我们的${role.teamName}做的${role.keywords[1]}，到底要不要先考虑${role.keywords[2]}？`,
    think:  `我画了一张草图，把${role.keywords[0]}放在中间，${role.keywords[1]}围在边上，老师你看可以吗？`,
    debate: `我比较了两版方案，第一版更简单，第二版更细致，我倾向第二版。`,
    act:    `我做完了！我把过程拍了3张照片，讲解稿写了 120 字。`,
  };
  const aiReplies = {
    learn:  '🌱 学的地层：这个发现很棒。再多问一层：它"决定"是从什么角度——形态？历史？还是使用方式？',
    ask:    '❓ 问的山口：好问题。把"要不要先"换成"为什么要先"，问题就会更深一层。',
    think:  '💭 思的绿洲：草图的结构合理。我想多问一句：为什么是这个中心，而不是别的？',
    debate: '🗣️ 辨的风谷：选第二版很好，但你能再说出两条"我不选第一版"的理由吗？',
    act:    '🚀 行的高地：辛苦了！讲解稿能不能用一句不超过 15 字的话开头？开头决定别人是否愿意听完。',
  };
  return [
    { role: 'student', text: stuOpeners[stage], at: '2026-05-13 09:15' },
    { role: 'ai',      text: aiReplies[stage],   at: '2026-05-13 09:15' },
    { role: 'student', text: '好，我再修改一版。', at: '2026-05-13 09:20' },
    { role: 'ai',      text: '✨ 我等你的下一版。', at: '2026-05-13 09:20' },
  ];
}

function seedSubmission(role, stage, name) {
  const baseText = {
    learn:  `我从资料里学到的3件事：1) ${role.keywords[0]}是核心；2) ${role.keywords[1]}和${role.keywords[2]}必须配合；3) 真实案例比想象更复杂。`,
    ask:    `我提出的关键问题：${role.stageTasks.ask}`,
    think:  `我的方案：${role.stageTasks.think.split('；')[0]}（已附草图）`,
    debate: `我选择的版本：第二版。理由：更兼顾${role.keywords[2]}，团队也都赞同。`,
    act:    `已完成 ${role.teamName} 的核心作品，并录制讲解。`,
  };
  const reflections = {
    learn:  `这一阶段我意识到，光"知道"还不够，要"用自己的话"讲出来才算真的懂。`,
    ask:    `提问比回答更难——好问题能让接下来的方案更聪明。`,
    think:  `设计方案就是不断在"想得到"和"做得出"之间妥协。`,
    debate: `辨析让我学会先复述对方的观点，再回应——这是我以前没做过的事。`,
    act:    `走完五阶段，最大的收获是：我不是一个人在做项目，团队和老师都在。`,
  };

  /* 媒体: 用 emoji+label 模拟 (避免大文件; 真实上传时存 dataURL) */
  const mediaSets = {
    learn:  [{ kind: 'image', label: '资料笔记', emoji: '📝' }],
    ask:    [{ kind: 'doc',   label: '问题清单', emoji: '📃' }],
    think:  [{ kind: 'image', label: '方案草图', emoji: '✏️' }, { kind: 'doc', label: '方案说明', emoji: '📄' }],
    debate: [{ kind: 'image', label: '对比图',   emoji: '🆚' }],
    act:    [{ kind: 'image', label: '作品照', emoji: '🖼️' }, { kind: 'image', label: '细节', emoji: '🔍' }, { kind: 'audio', label: '讲解录音', emoji: '🎙️' }],
  };

  return {
    text: baseText[stage],
    reflection: reflections[stage],
    media: mediaSets[stage].map((m, i) => ({
      id: `m-${stage}-${i}-${Math.random().toString(36).slice(2,7)}`,
      ...m,
      uploadedAt: '2026-05-13',
      data: null,
    })),
    submittedAt: '2026-05-13',
  };
}

/* ============== 工具：班级 / 角色聚合查询 ============== */

function studentsByClass(state, className) {
  return Object.values(state.students).filter(s => s.class === className);
}
function studentsByRole(state, roleId) {
  return Object.values(state.students).filter(s => s.roleId === roleId);
}
function classProgressSummary(state, className) {
  const list = studentsByClass(state, className);
  const stageCount = { learn: 0, ask: 0, think: 0, debate: 0, act: 0, done: 0 };
  list.forEach(s => {
    const completedAll = STAGES.every(st => s.stages[st.key].status === 'completed');
    if (completedAll) { stageCount.done++; return; }
    stageCount[s.currentStage]++;
  });
  return { total: list.length, stageCount };
}
function studentOverallScore(student) {
  const scored = Object.values(student.stages).filter(s => s.teacherScore);
  if (scored.length === 0) return null;
  const sum = scored.reduce((acc, s) => acc + (s.teacherScore.task + s.teacherScore.thinking + s.teacherScore.evidence + s.teacherScore.express) / 4, 0);
  return Math.round(sum / scored.length * 10) / 10;
}
